// ---------------------------------------------------------------------------
// Icon Text Renderer
// ---------------------------------------------------------------------------
// Marketing primitive — publishes the editor `IconText` block as a small
// benefit row pairing a glyph/emoji with a heading + description. Layout
// is constrained to `horizontal` or `vertical`. The icon is rendered as
// text content (the editor stores emoji/unicode), so it is escaped, not
// inserted as HTML.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

type IconTextLayout = "horizontal" | "vertical";

interface IconTextProps {
  icon?: string;
  heading?: string;
  description?: string;
  layout?: IconTextLayout | string;
}

const VALID_LAYOUTS: ReadonlySet<IconTextLayout> = new Set([
  "horizontal",
  "vertical",
]);

function resolveLayout(value: unknown): IconTextLayout {
  if (typeof value === "string" && VALID_LAYOUTS.has(value as IconTextLayout)) {
    return value as IconTextLayout;
  }
  return "horizontal";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as IconTextProps;

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  if (!heading) {
    return "<!-- icon-text missing heading -->";
  }

  const icon = typeof p.icon === "string" ? p.icon.trim() : "";
  const description = typeof p.description === "string" ? p.description.trim() : "";
  const layout = resolveLayout(p.layout);

  const iconHtml = icon
    ? `\n    <span class="ease-icon-text__icon" aria-hidden="true">${text(icon)}</span>`
    : "";
  const descriptionHtml = description
    ? `\n      <p class="ease-icon-text__description">${text(description)}</p>`
    : "";

  return `<section class="ease-section ease-icon-text ease-icon-text--${attr(layout)}">${iconHtml}
    <div class="ease-icon-text__body">
      <h3 class="ease-icon-text__heading">${text(heading)}</h3>${descriptionHtml}
    </div>
</section>`;
}

registerRenderer("icon-text", { render });
